import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { UiLink } from "./link";
import { cn, focusRing } from "./utils";

export type IconButtonVariant = "outline" | "soft" | "linen" | "ghost" | "primary" | "danger";

const variants: Record<IconButtonVariant, string> = {
  outline: "border border-line bg-surface text-ink hover:bg-linen",
  soft: "bg-surface-header text-ink hover:bg-surface-low",
  linen: "bg-linen text-ink hover:bg-surface",
  ghost: "bg-transparent text-ink hover:bg-surface-header",
  primary: "bg-blue text-white hover:bg-blue-dark",
  danger: "bg-scarlet text-white hover:brightness-95",
};

type CommonProps = {
  /** Read by screen readers. Icon buttons have no visible text, so this is required. */
  label: string;
  variant?: IconButtonVariant;
  shape?: "square" | "circle";
  size?: number;
  className?: string;
  children: ReactNode;
};

function classesFor({ variant = "ghost", shape = "square", className }: Partial<CommonProps>) {
  return cn(
    "relative inline-flex flex-none items-center justify-center cursor-pointer transition-colors",
    "disabled:cursor-not-allowed disabled:opacity-50",
    shape === "circle" ? "rounded-full" : "rounded-field",
    variants[variant],
    focusRing,
    className,
  );
}

export type IconButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> & { href?: undefined };

export type IconLinkProps = CommonProps & { href: string; "aria-current"?: "page" | boolean };

export function IconButton(props: IconButtonProps | IconLinkProps) {
  const { label, variant, shape, size = 44, className, children } = props;
  const style: CSSProperties = { width: size, height: size };
  const classes = classesFor({ variant, shape, className });
  if (props.href !== undefined) {
    return (
      <UiLink href={props.href} aria-label={label} className={classes} style={style}>
        {children}
      </UiLink>
    );
  }
  const {
    label: _l,
    variant: _v,
    shape: _s,
    size: _z,
    className: _c,
    children: _ch,
    type = "button",
    ...rest
  } = props as IconButtonProps;
  return (
    <button type={type} aria-label={label} className={classes} style={style} {...rest}>
      {children}
    </button>
  );
}
